/* eslint-disable indent */
import {
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { Business as BusinessIcon } from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import AutoCard from 'components/AutoCard';
import { StatusIcon } from 'components/TableItems';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import i18n from 'i18n';
import { CompanyFields } from 'modules/companies/interfaces';
import React from 'react';
import { getCompanyName } from 'utils/tsHelpers';
import { Formik } from 'formik';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import Button from 'components/Button';
import { editCompanyNamesRequest } from 'modules/companies/actions';
import ApplicationState from 'store/interfaces';
import styles from './styles';

const useStyles = makeStyles(styles);

interface CompanyCardProps {
  company: CompanyFields | null;
  permissions: PermissionsData | undefined;
  editCompanyNamesRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
}

const CompanyCard = ({
  company,
  permissions,
  editCompanyNamesRequest,
  modalClose,
  modalOpen,
}: CompanyCardProps) => {
  const classes = useStyles();
  const [companyAlias, setCompanyAlias] = React.useState(false);

  const handleSubmit = values => {
    editCompanyNamesRequest({
      id: company?.company_uuid,
      values,
    });
    modalClose();
  };

  return (
    <AutoCard>
      <Grid container className={classes.companyCard}>
        <Grid container alignItems="center" className={classes.cardHeader}>
          <BusinessIcon className={classes.companyIcon} />
          <Typography className={classes.companyName}>
            {getCompanyName(company)}
          </Typography>
        </Grid>
        <Grid container>
          <Grid item xs={4} className={classes.companyFields}>
            <Grid className={classes.companyInfo}>
              <Typography className={classes.companyField}>{`${i18n.t(
                'ZOHO_ID_KEY'
              )}:`}</Typography>
              <Typography className={classes.companyValue}>
                {company?.zoho_id}
              </Typography>
            </Grid>
            <Grid className={classes.companyInfo}>
              <Typography className={classes.companyField}>{`${i18n.t(
                'LEGAL_NAME_KEY'
              )}:`}</Typography>
              <Tooltip title={company?.legal_name}>
                <Typography className={classes.companyValue}>
                  {company?.legal_name || '-'}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid className={classes.companyInfo}>
              <Typography className={classes.companyField}>
                {`${i18n.t('SINGLE_CORPORATE_NAME_KEY')}:`}
              </Typography>
              <Tooltip title={company?.trading_name}>
                <Typography className={classes.companyValue}>
                  {company?.trading_name || '-'}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid className={classes.companyInfo}>
              <Typography className={classes.companyField}>{`${i18n.t(
                'COMPANY_NAME_CRM_KEY'
              )}:`}</Typography>
              <Tooltip title={company?.company_name}>
                <Typography className={classes.companyValue}>
                  {company?.company_name || '-'}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid className={classes.companyInfo}>
              <Typography className={classes.companyField}>
                {`${i18n.t('ALIAS_NAME_KEY')}:`}
              </Typography>
              {!companyAlias && (
                <Tooltip title={company?.alias}>
                  <Typography className={classes.companyValue}>
                    {company?.alias}
                  </Typography>
                </Tooltip>
              )}
              {!company?.alias &&
                !companyAlias &&
                permissions?.can_edit_company_alias && (
                  <Button
                    onClick={() => setCompanyAlias(true)}
                    className={classes.editAliasButton}
                    endIcon={<EditIcon />}
                  >
                    {i18n.t('CREATE_ALIAS_NAME_KEY')}
                  </Button>
                )}
              {company?.alias &&
                !companyAlias &&
                permissions?.can_edit_company_alias && (
                  <IconButton className={classes.editAlias}>
                    <EditIcon onClick={() => setCompanyAlias(true)} />
                  </IconButton>
                )}
              {!company?.alias && !permissions?.can_edit_company_alias && (
                <Typography className={classes.companyValue}>{'-'}</Typography>
              )}
              {companyAlias && (
                <Formik
                  initialValues={{ alias: company?.alias }}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ submitForm, setFieldValue, errors, values }) => (
                    <Grid
                      container
                      xs={12}
                      justify="space-between"
                      className={classes.changeName}
                    >
                      <Grid container xs={12} spacing={2}>
                        <GenericForm
                          fields={values}
                          errors={errors}
                          screenFields={[
                            {
                              gridXs: 12,
                              stateKey: 'alias',
                              label: i18n.t('ALIAS_NAME_KEY'),
                            },
                          ]}
                          setFieldValue={(key, value) => {
                            setFieldValue(key, value);
                          }}
                          overrideClasses={{
                            selectLabel: { fontSize: 12, fontWeight: 500 },
                          }}
                        />
                      </Grid>
                      <DoneIcon
                        className={classes.doneIcon}
                        onClick={() =>
                          modalOpen({
                            title: i18n.t('CONFIRM_COMPANY_ALIAS_TITLE_KEY'),
                            content: (
                              <Typography>
                                {i18n.t('CONFIRM_COMPANY_ALIAS_KEY', {
                                  alias: values.alias,
                                })}
                              </Typography>
                            ),
                            fullWidth: true,
                            footer: (
                              <Grid>
                                <Button
                                  submitButton
                                  id="confirm-button"
                                  variant="outlined"
                                  onClick={submitForm}
                                >
                                  {i18n.t('CONFIRM_KEY')}
                                </Button>
                                <Button
                                  id="cancel-button"
                                  variant="outlined"
                                  onClick={modalClose}
                                >
                                  {i18n.t('CANCEL_KEY')}
                                </Button>
                              </Grid>
                            ),
                          })
                        }
                      />
                      <CloseIcon
                        className={classes.closeIcon}
                        onClick={() => {
                          setCompanyAlias(false);
                        }}
                      />
                    </Grid>
                  )}
                </Formik>
              )}
            </Grid>
          </Grid>
          <Grid item xs={4} className={classes.companyFields}>
            <Grid className={classes.companyInfo}>
              <Typography className={classes.companyField}>Status:</Typography>
              <Tooltip
                title={i18n.t(
                  company?.portal_deleted
                    ? 'COMPANIES_PAGE.PORTAL_DELETED_TEXT_KEY'
                    : 'ACTIVE_KEY'
                )}
              >
                <Grid className={classes.statusValue}>
                  <Typography className={classes.companyValue}>
                    {i18n.t(
                      company?.portal_deleted ? 'PENDING_KEY' : 'ACTIVE_KEY'
                    )}
                  </Typography>
                  <StatusIcon
                    status={company?.portal_deleted ? 'blocked' : 'success'}
                  />
                </Grid>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AutoCard>
  );
};

const mapStateToProps = ({ authenticate: { data } }: ApplicationState) => ({
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  editCompanyNamesRequest,
})(CompanyCard);
